import { ContractorSearchFormValues } from '@hooks/useContractorSearch';
import {
  useSearchForMemberByAddressMutation,
  useSearchMemberMutation,
} from '@store/rtk/members';
import { useAppSelector } from '@store/hooks';
import { selectHomePageModalSearch } from '@store/slices/search/homeSearchSlice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from '@hooks/useDirtyForm';
import {
  defaultValues,
  FormTypes,
} from '@src/modals/HomeModals/MemberSearchModal/constants';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useWatch } from 'react-hook-form';
import { encrypt, getLocationAsText } from '@src/utils';
import { idType } from '@src/types';

function buildFilters(
  values: Partial<ContractorSearchFormValues>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(values)) {
    if (value === null || value === undefined || value === '') continue;
    result[key] = value;
  }

  return result;
}

export const useMemberSearch = () => {
  const [searchMember, { isLoading }] = useSearchMemberMutation();
  const [setSearchRequestByMember, { data }] =
    useSearchForMemberByAddressMutation();

  const searchMemberRef = useRef(searchMember);
  const setSearchRequestByMemberRef = useRef(setSearchRequestByMember);

  useEffect(() => {
    searchMemberRef.current = searchMember;
  });

  useEffect(() => {
    setSearchRequestByMemberRef.current = setSearchRequestByMember;
  });

  const { searchMemberData } = useAppSelector(selectHomePageModalSearch);

  const [filters, setFilters] = useState<
    Record<string, string | number | null>
  >({});
  const [address, setAddressLocation] = useState<null | number>(null);
  const [addressPrompt, setAddressPrompt] = useState<null | string>(null);

  const prevFiltersRef = useRef<string>('');
  const prevAddressPromptRef = useRef<string>('');

  const { control, setValue } = useForm<FormTypes>({
    defaultValues,
    mode: 'onChange',
  });

  const debouncedAddressValues = useDebouncedValue<string | null>(
    addressPrompt,
    500
  );

  useEffect(() => {
    if (!debouncedAddressValues) return;

    const serialized = String(debouncedAddressValues);
    if (serialized === prevAddressPromptRef.current) return;
    prevAddressPromptRef.current = serialized;

    setSearchRequestByMemberRef.current({ query: debouncedAddressValues });
  }, [debouncedAddressValues]);

  const watchedFormValues = useWatch<FormTypes>({ control });

  const debouncedValues = useDebouncedValue<FormTypes>(
    { ...watchedFormValues, address } as FormTypes,
    500
  );

  const handleChangeMemberAddress = useCallback(
    (_name: string, value: string) => {
      if (!value) {
        setAddressPrompt(null);
        setAddressLocation(null);
      } else {
        setAddressPrompt(value);
      }
    },
    []
  );

  useEffect(() => {
    if (!debouncedValues) return;

    const cleanedValues = buildFilters(
      debouncedValues as Partial<ContractorSearchFormValues>
    );

    const serialized = JSON.stringify(cleanedValues);
    if (serialized === prevFiltersRef.current) return;
    prevFiltersRef.current = serialized;

    setFilters(cleanedValues as any);
    searchMemberRef.current({ argument: { body: cleanedValues } });
  }, [debouncedValues]);

  const handleClickEdit = useCallback((id: idType) => {
    const encodeId = encrypt({ member: id });
    window.open(`/members/information/${encodeId}`, '_blank');
  }, []);

  const handleSelectAddress = useCallback(
    (elem: any) => {
      setAddressLocation(elem.id);
      setValue('address', getLocationAsText(elem));
      setAddressPrompt(null);
    },
    [setValue]
  );

  return {
    control,
    filters,
    handleClickEdit,
    handleChangeMemberAddress,
    handleSelectAddress,
    searchMemberData,
    isLoading,
    data,
    debouncedAddressValues,
  };
};
